import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TableLoader } from '../../components/common/Loader';
import ReactDataTable from 'react-data-table-component';
import {
    IconPlus, IconEye, IconEdit, IconTrash, IconLoader2,
    IconPlaneTilt,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import ActionDropdown from '../../components/common/ActionDropdown';
import { useMyTravelRequests, useCreateTravelRequest } from '../../hooks/useTravel';

const TRANSPORT_MODES = [
    { value: 'road', label: 'By Road' },
    { value: 'air', label: 'By Air' },
    { value: 'official', label: 'Official Vehicle' },
    { value: 'other', label: 'Other' },
];

const STATUS_BADGE = {
    draft:              'bg-bg-light text-text-secondary border border-border',
    submitted:          'bg-sky/10 text-sky border border-sky/30',
    lm_approved:        'bg-amber-500/10 text-amber-500 border border-amber-500/30',
    rejected:           'bg-danger/10 text-danger border border-danger/30',
    approved:           'bg-success/10 text-success border border-success/30',
    budget_submitted:   'bg-sky/10 text-sky border border-sky/30',
    budget_approved:    'bg-teal-500/10 text-teal-500 border border-teal-500/30',
    advance_submitted:  'bg-amber-500/10 text-amber-500 border border-amber-500/30',
    advance_approved:   'bg-indigo-500/10 text-indigo-500 border border-indigo-500/30',
    advance_paid:       'bg-success/10 text-success border border-success/30',
    prv_submitted:      'bg-sky/10 text-sky border border-sky/30',
    prv_approved:       'bg-indigo-500/10 text-indigo-500 border border-indigo-500/30',
    completed:          'bg-success/20 text-success border border-success/40',
};

const STATUS_LABELS = {
    draft:              'Draft',
    submitted:          'Submitted',
    lm_approved:        'LM Approved',
    rejected:           'Rejected',
    approved:           'Approved',
    budget_submitted:   'Budget Submitted',
    budget_approved:    'Budget Approved',
    advance_submitted:  'Advance Submitted',
    advance_approved:   'Advance Approved',
    advance_paid:       'Advance Paid',
    prv_submitted:      'PRV Submitted',
    prv_approved:       'PRV Approved',
    completed:          'Completed',
};

function getWorkflowStatus(row) {
    const req = row.status;
    const lm = row.line_manager_status;
    const sec = row.security_manager_status;
    const prv = row.prv_status;
    const adv = row.advance_status;
    const bud = row.budget_status;

    // Most advanced stage first
    if (prv === 'paid') return 'completed';
    if (prv === 'approved') return 'prv_approved';
    if (prv === 'submitted') return 'prv_submitted';
    if (adv === 'paid') return 'advance_paid';
    if (adv === 'approved') return 'advance_approved';
    if (adv === 'submitted') return 'advance_submitted';
    if (bud === 'approved') return 'budget_approved';
    if (bud === 'submitted') return 'budget_submitted';
    if (req === 'approved') return 'approved';
    if (req === 'rejected') return 'rejected';
    // Intermediate: LM approved but security pending
    if (lm === 'approved' && sec !== 'approved') return 'lm_approved';
    return req || 'draft';
}

function StatusBadge({ row }) {
    const key = getWorkflowStatus(row);
    const cls = STATUS_BADGE[key] || 'bg-bg-light text-text-secondary border border-border';
    return (
        <span className={`px-8 py-3 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
            {STATUS_LABELS[key] || key}
        </span>
    );
}

function EmptyLeg() {
    return { departure_date: '', return_date: '', state_city: '', estimated_nights: '', mode: 'road' };
}

function CreateTravelRequestModal({ isOpen, onClose, orgId }) {
    const navigate = useNavigate();
    const createRequest = useCreateTravelRequest();

    const [purpose, setPurpose] = useState('');
    const [currency, setCurrency] = useState('NGN');
    const [costCentre, setCostCentre] = useState('');
    const [specialInstructions, setSpecialInstructions] = useState('');
    const [legs, setLegs] = useState([EmptyLeg()]);

    const resetForm = () => {
        setPurpose('');
        setCurrency('NGN');
        setCostCentre('');
        setSpecialInstructions('');
        setLegs([EmptyLeg()]);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleAddLeg = () => {
        setLegs(prev => [...prev, EmptyLeg()]);
    };

    const handleRemoveLeg = (idx) => {
        setLegs(prev => prev.filter((_, i) => i !== idx));
    };

    const handleLegChange = (idx, field, value) => {
        setLegs(prev => prev.map((leg, i) => {
            if (i !== idx) return leg;
            const updated = { ...leg, [field]: value };
            if (field === 'departure_date' || field === 'return_date') {
                const dep = field === 'departure_date' ? value : leg.departure_date;
                const ret = field === 'return_date' ? value : leg.return_date;
                if (dep && ret) {
                    const nights = Math.max(0, Math.round((new Date(ret) - new Date(dep)) / (1000 * 60 * 60 * 24)));
                    updated.estimated_nights = nights;
                }
            }
            return updated;
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!purpose.trim()) {
            toast.error('Purpose of trip is required.');
            return;
        }
        const hasValidLeg = legs.some(l => l.departure_date && l.state_city);
        if (!hasValidLeg) {
            toast.error('Please fill in at least one trip leg with a date and destination.');
            return;
        }

        const data = {
            purpose: purpose.trim(),
            currency,
            cost_centre: costCentre.trim(),
            special_instructions: specialInstructions.trim(),
            legs: legs.filter(l => l.departure_date && l.state_city).map(l => ({
                departure_date: l.departure_date,
                return_date: l.return_date || null,
                state_city: l.state_city,
                estimated_nights: l.estimated_nights ? parseInt(l.estimated_nights, 10) : 0,
                mode_of_transport: l.mode,
            })),
        };

        createRequest.mutate({ data, orgId }, {
            onSuccess: (res) => {
                toast.success('Travel request created.');
                handleClose();
                navigate(`/travel/${res.id}`);
            },
            onError: (err) => {
                const msg = err?.response?.data?.detail || err?.response?.data?.error || err.message || 'Failed to create request.';
                toast.error(msg);
            },
        });
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-15"
            onClick={handleClose}
        >
            <div
                className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[620px] max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-20 border-b border-border">
                    <h5 className="text-[16px]/[22px] font-semibold text-center">New Travel Request</h5>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-20 flex flex-col gap-15">
                        {/* Purpose */}
                        <div className="form-group">
                            <label className="block text-sm font-medium mb-5">
                                Purpose of Trip <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                value={purpose}
                                onChange={(e) => setPurpose(e.target.value)}
                                placeholder="Purpose of trip"
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                            />
                        </div>

                        {/* Currency */}
                        <div className="form-group">
                            <label className="block text-sm font-medium mb-5">Currency <span className="text-danger">*</span></label>
                            <select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                            >
                                <option value="NGN">NGN — Nigerian Naira (₦)</option>
                                <option value="USD">USD — US Dollar ($)</option>
                                <option value="GBP">GBP — British Pound (£)</option>
                                <option value="EUR">EUR — Euro (€)</option>
                                <option value="CAD">CAD — Canadian Dollar (C$)</option>
                                <option value="ZAR">ZAR — South African Rand (R)</option>
                                <option value="KES">KES — Kenyan Shilling (KSh)</option>
                                <option value="GHS">GHS — Ghanaian Cedi (GH₵)</option>
                            </select>
                        </div>

                        {/* Cost Centre */}
                        <div className="form-group">
                            <label className="block text-sm font-medium mb-5">Cost Centre / Budget Line</label>
                            <input
                                type="text"
                                value={costCentre}
                                onChange={(e) => setCostCentre(e.target.value)}
                                placeholder="Cost Centre / Budget Line"
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                            />
                        </div>

                        {/* Special Instructions */}
                        <div className="form-group">
                            <label className="block text-sm font-medium mb-5">Special Instructions</label>
                            <textarea
                                value={specialInstructions}
                                onChange={(e) => setSpecialInstructions(e.target.value)}
                                placeholder="Any special travel requirements..."
                                rows={3}
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                            />
                        </div>

                        {/* Trip Legs */}
                        <div>
                            <div className="flex items-center justify-between mb-10">
                                <label className="text-sm font-medium">Trip Legs</label>
                                <button
                                    type="button"
                                    onClick={handleAddLeg}
                                    className="btn btn-outline-secondary text-xs px-10 py-5 flex items-center gap-5"
                                >
                                    <IconPlus size={13} /> Add Leg
                                </button>
                            </div>

                            <div className="flex flex-col gap-10">
                                {legs.map((leg, idx) => (
                                    <div key={idx} className="bg-bg-light rounded-lg border border-border p-12">
                                        <div className="flex items-center justify-between mb-8">
                                            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                                Leg {idx + 1}
                                            </span>
                                            {legs.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveLeg(idx)}
                                                    className="text-danger hover:text-danger/80 text-xs"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-10">
                                            <div className="form-group">
                                                <label className="block text-xs font-medium mb-4">Departure Date</label>
                                                <input
                                                    type="date"
                                                    value={leg.departure_date}
                                                    onChange={(e) => handleLegChange(idx, 'departure_date', e.target.value)}
                                                    className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="block text-xs font-medium mb-4">Return Date</label>
                                                <input
                                                    type="date"
                                                    value={leg.return_date}
                                                    onChange={(e) => handleLegChange(idx, 'return_date', e.target.value)}
                                                    className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="block text-xs font-medium mb-4">State / City</label>
                                                <input
                                                    type="text"
                                                    value={leg.state_city}
                                                    onChange={(e) => handleLegChange(idx, 'state_city', e.target.value)}
                                                    placeholder="City / Location"
                                                    className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="block text-xs font-medium mb-4">Estimated Nights</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={leg.estimated_nights}
                                                    readOnly
                                                    placeholder="Auto-calculated"
                                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none cursor-default text-text-secondary"
                                                />
                                            </div>
                                            <div className="form-group col-span-2">
                                                <label className="block text-xs font-medium mb-4">Mode of Transport</label>
                                                <select
                                                    value={leg.mode}
                                                    onChange={(e) => handleLegChange(idx, 'mode', e.target.value)}
                                                    className="w-full bg-bg-dark border border-border rounded-[4px] px-10 py-[7px] text-sm focus:outline-none"
                                                >
                                                    {TRANSPORT_MODES.map(m => (
                                                        <option key={m.value} value={m.value}>{m.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex gap-10 p-20 border-t border-border">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn btn-outline-secondary flex-1"
                            disabled={createRequest.isPending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createRequest.isPending}
                            className="btn btn-primary flex-1 flex items-center justify-center gap-5"
                        >
                            {createRequest.isPending ? (
                                <IconLoader2 size={16} className="animate-spin" />
                            ) : (
                                <IconPlaneTilt size={16} />
                            )}
                            {createRequest.isPending ? 'Creating...' : 'Create Request'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function Travel() {
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const canSelf = hasAnyPermission(['rbacapp.trv_self', 'rbacapp.trv_security', 'rbacapp.trv_finance', 'rbacapp.trv_partner', 'rbacapp.trv_view_all']);
    const canAccess = canSelf;

    const { data: requestsData, isLoading } = useMyTravelRequests(orgId);
    const requests = requestsData || [];

    const [createModal, setCreateModal] = useState(false);
    const [filterText, setFilterText] = useState('');

    const formatDate = (d) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const getDestinationSummary = (legs) => {
        if (!legs || legs.length === 0) return '-';
        return legs.map(l => l.state_city || l.destination).filter(Boolean).join(' → ') || '-';
    };

    const getFirstDate = (legs) => {
        if (!legs || legs.length === 0) return '-';
        const dates = legs.map(l => l.departure_date).filter(Boolean).sort();
        return dates[0] ? formatDate(dates[0]) : '-';
    };

    const columns = [
        {
            name: 'Purpose',
            selector: row => row.purpose,
            sortable: true,
            cell: row => (
                <div>
                    <p className="font-medium text-sm">{row.purpose || '-'}</p>
                    {row.cost_centre && (
                        <p className="text-xs text-text-secondary mt-1">{row.cost_centre}</p>
                    )}
                </div>
            ),
            minWidth: '220px',
        },
        {
            name: 'Destination',
            selector: row => getDestinationSummary(row.legs),
            cell: row => (
                <span className="text-sm">{getDestinationSummary(row.legs)}</span>
            ),
            minWidth: '180px',
        },
        {
            name: 'Date',
            selector: row => getFirstDate(row.legs),
            cell: row => (
                <span className="text-sm text-text-secondary">{getFirstDate(row.legs)}</span>
            ),
            width: '130px',
        },
        {
            name: 'Status',
            selector: row => getWorkflowStatus(row),
            cell: row => <StatusBadge row={row} />,
            sortable: true,
            width: '180px',
        },
        {
            name: 'Actions',
            cell: row => {
                const isDraft = row.status === 'draft';
                return (
                    <ActionDropdown
                        menuItems={[
                            {
                                label: 'View Details',
                                icon: IconEye,
                                onClick: () => navigate(`/travel/${row.id}`),
                                description: 'View full travel request',
                            },
                            ...(isDraft && canSelf ? [
                                {
                                    label: 'Edit Request',
                                    icon: IconEdit,
                                    onClick: () => navigate(`/travel/${row.id}`),
                                    description: 'Edit this draft request',
                                },
                            ] : []),
                        ]}
                    />
                );
            },
            width: '80px',
            center: true,
        },
    ];

    const filteredItems = useMemo(() => {
        if (!filterText) return requests;
        const q = filterText.toLowerCase();
        return requests.filter(r =>
            (r.purpose || '').toLowerCase().includes(q) ||
            (STATUS_LABELS[getWorkflowStatus(r)] || r.status || '').toLowerCase().includes(q) ||
            getDestinationSummary(r.legs).toLowerCase().includes(q)
        );
    }, [requests, filterText]);

    const subHeaderComponent = useMemo(() => (
        <div className="flex gap-5 mb-10 w-full justify-end">
            <input
                type="text"
                placeholder="Search requests..."
                className="bg-bg-light border border-border w-full max-w-[250px] py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
            />
        </div>
    ), [filterText]);

    if (!canAccess) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Travel Requests" path="Travel / My Requests" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    You do not have permission to access travel requests.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="Travel Requests" path="Travel / My Requests" />

            {/* Main Table Card */}
            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                <div className="flex justify-between items-center mb-20 flex-wrap gap-10">
                    <h2 className="text-lg font-bold">My Travel Requests</h2>
                    {canSelf && (
                        <button
                            onClick={() => setCreateModal(true)}
                            className="btn btn-primary flex items-center gap-5"
                        >
                            <IconPlus size={16} />
                            New Request
                        </button>
                    )}
                </div>

                {isLoading ? (
                    <TableLoader />
                ) : (
                    <ReactDataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        subHeader
                        subHeaderComponent={subHeaderComponent}
                        highlightOnHover
                        pointerOnHover
                        persistTableHead
                        onRowClicked={(row) => navigate(`/travel/${row.id}`)}
                        noDataComponent={
                            <div className="py-40 text-center text-text-secondary text-sm">
                                No travel requests found. Click "New Request" to get started.
                            </div>
                        }
                        customStyles={{
                            table: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-primary)' } },
                            headRow: { style: { backgroundColor: 'var(--bg-light)', borderBottomColor: 'var(--border)', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' } },
                            headCells: { style: { backgroundColor: 'transparent', color: 'var(--text-secondary)', paddingLeft: '16px', paddingRight: '16px' } },
                            rows: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-primary)', borderBottomColor: 'var(--border)', fontSize: '13px' }, highlightOnHoverStyle: { backgroundColor: 'var(--bg-light)', color: 'var(--text-primary)', borderBottomColor: 'var(--border)', outline: 'none' } },
                            cells: { style: { paddingLeft: '16px', paddingRight: '16px', color: 'var(--text-primary)' } },
                            pagination: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-secondary)', borderTopColor: 'var(--border)' }, pageButtonsStyle: { color: 'var(--text-secondary)', fill: 'var(--text-secondary)', '&:hover:not(:disabled)': { backgroundColor: 'var(--bg-light)' }, '&:focus': { backgroundColor: 'var(--bg-light)' } } },
                            subHeader: { style: { backgroundColor: 'var(--bg-dark)', paddingLeft: '0', paddingRight: '0' } },
                            noData: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-secondary)' } },
                        }}
                    />
                )}
            </div>

            {/* Create Modal */}
            <CreateTravelRequestModal
                isOpen={createModal}
                onClose={() => setCreateModal(false)}
                orgId={orgId}
            />
        </div>
    );
}
